// MY NEXT ACTIONS on the Quotes page (ux-guided-deal, item 7).
//
// Built ONLY from existing read routes, one call each, never a per-quote loop:
//   * my drafts whose OEM prices are not all confirmed -- quote_list
//     status=draft (the server scopes drafts to their owner; an Admin's own
//     are asked for with owner=<me>), each row's derived OEM counts;
//   * OEM orders waiting for an acknowledgement, late or at risk --
//     open_oem_orders (the server scopes: Sales their own quotes', Admin all).
// M4 (D195, a) added the two reads D193 lacked, still one call each:
//   * my finalised quotes waiting for the customer -- quote_list status=open
//     &mine=1 (mine narrows the scope for every role, never widens it);
//   * my Won quotes not yet fully ordered -- quote_list status=won&mine=1,
//     each row's server-computed left_to_order.
//
// Loaded when opened, so the list page makes no extra call until asked. The
// open/closed choice is remembered per browser; storage is a convenience.

import { useCallback, useEffect, useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import { getOpenOemOrders, listQuotesPage, type OemOrder, type QuoteListRow } from "@/services/api";
import { STATUS_LABEL } from "@/components/OemOrderStatus";
import { semanticColors, colors, radius, spacing, heights, typography, iconSize, iconStroke } from "@/styles/tokens";

const KEY = "anixify.myNextActions.open";
const SHOW = 5;

interface Item { id: string; quoteId: number; text: string; tone: "amber" | "red" | "gray" }

export function MyNextActions({ onOpenQuote }: {
  /** Kept for callers; "mine" is now the server's, for every role (M4). */
  isAdmin?: boolean; uid?: number | null; onOpenQuote: (quoteId: number) => void;
}) {
  const [open, setOpen] = useState<boolean>(() => {
    try { return window.localStorage.getItem(KEY) === "1"; } catch { return false; }
  });
  const [drafts, setDrafts] = useState<Item[] | null>(null);
  const [waiting, setWaiting] = useState<Item[] | null>(null);
  const [unordered, setUnordered] = useState<Item[] | null>(null);
  const [orders, setOrders] = useState<Item[] | null>(null);
  const [draftsMore, setDraftsMore] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setError("");
    const mine = (status: string) => ({ status, mine: "1", page: "1", page_size: "100", sort: "date", dir: "desc" });
    const [q, o, w, won] = await Promise.all([listQuotesPage(mine("draft")), getOpenOemOrders({}),
      listQuotesPage(mine("open")), listQuotesPage(mine("won"))]);
    if (!w.ok || !w.page) { setWaiting(null); setError((e) => e || (w.error ?? "Your quotes could not be read.")); }
    else {
      setWaiting(w.page.quotes.map((r: QuoteListRow) => ({
        id: `w${r.quote_id}`, quoteId: r.quote_id, tone: (r.days_left != null && r.days_left <= 3 ? "amber" : "gray") as Item["tone"],
        text: `${r.name}${r.partner_name ? ` · ${r.partner_name}` : ""}: waiting for the customer`
          + (r.days_left != null ? ` (valid ${r.days_left >= 0 ? `${r.days_left} more day${r.days_left === 1 ? "" : "s"}` : "expired"})` : ""),
      })));
    }
    if (!won.ok || !won.page) { setUnordered(null); setError((e) => e || (won.error ?? "Your quotes could not be read.")); }
    else {
      setUnordered(won.page.quotes.filter((r: QuoteListRow) => (r.left_to_order ?? 0) > 1e-9).map((r: QuoteListRow) => ({
        id: `u${r.quote_id}`, quoteId: r.quote_id, tone: "amber" as Item["tone"],
        text: `${r.name}${r.partner_name ? ` · ${r.partner_name}` : ""}: Won, ${r.left_to_order} still to order from OEMs`,
      })));
    }
    if (!q.ok || !q.page) { setDrafts(null); setError(q.error ?? "Your drafts could not be read."); }
    else {
      const rows = q.page.quotes.filter((r: QuoteListRow) => r.oem && r.oem.priced_lines > 0
        && r.oem.confirmed_lines < r.oem.priced_lines);
      setDraftsMore(q.page.total > q.page.quotes.length);
      setDrafts(rows.map((r) => ({
        id: `d${r.quote_id}`, quoteId: r.quote_id, tone: "amber",
        text: `${r.name}${r.partner_name ? ` · ${r.partner_name}` : ""}: ${r.oem!.confirmed_lines} of ${r.oem!.priced_lines} OEM prices confirmed`,
      })));
    }
    if (!o.ok) { setOrders(null); setError((e) => e || (o.error ?? "The OEM orders could not be read.")); }
    else {
      const pick = (o.orders ?? []).filter((x: OemOrder) => x.late || x.at_risk || x.status === "placed");
      pick.sort((a, b) => Number(!!b.late) - Number(!!a.late) || Number(!!b.at_risk) - Number(!!a.at_risk));
      setOrders(pick.map((x) => ({
        id: `o${x.id}`, quoteId: x.quote_id, tone: x.late ? "red" : x.at_risk ? "amber" : "gray",
        text: `${x.number} (${x.oem}, ${x.quote_name}): ${x.late ? "LATE" : x.at_risk ? "at risk"
          : "waiting for the OEM to acknowledge"} · ${STATUS_LABEL[x.status] ?? x.status}`,
      })));
    }
  }, []);

  useEffect(() => { if (open) void load(); }, [open, load]);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    try { if (next) window.localStorage.setItem(KEY, "1"); else window.localStorage.removeItem(KEY); } catch { /* blocked */ }
  };

  const group = (title: string, items: Item[] | null, empty: string, more = false, testId = "") => (
    <div data-testid={testId} style={{ display: "grid", gap: spacing[4] }}>
      <div style={{ fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.semibold, color: semanticColors.textPrimary }}>
        {title}{items ? ` (${items.length}${more ? "+" : ""})` : ""}
      </div>
      {items === null ? <div style={{ fontSize: typography.fontSize.sm, color: semanticColors.textMuted }}>—</div>
        : items.length === 0 ? <div style={{ fontSize: typography.fontSize.sm, color: semanticColors.textMuted }}>{empty}</div>
        : (
          <ul style={{ margin: 0, padding: 0, listStyle: "none", display: "grid", gap: spacing[2] }}>
            {items.slice(0, SHOW).map((it) => (
              <li key={it.id}>
                <button type="button" data-testid={`next-action-${it.id}`} onClick={() => onOpenQuote(it.quoteId)} style={{
                  background: "none", border: "none", padding: 0, cursor: "pointer", textAlign: "left",
                  fontSize: typography.fontSize.sm, textDecoration: "underline",
                  color: it.tone === "red" ? colors.red600 : it.tone === "amber" ? colors.amber800 : semanticColors.textSecondary,
                }}>{it.text}</button>
              </li>
            ))}
            {items.length > SHOW && (
              <li style={{ fontSize: typography.fontSize.xs, color: semanticColors.textMuted }}>and {items.length - SHOW} more</li>
            )}
          </ul>
        )}
    </div>
  );

  return (
    <section data-testid="my-next-actions" aria-label="My next actions" style={{
      marginBottom: spacing[12], border: `1px solid ${semanticColors.border}`, borderRadius: radius.base,
      backgroundColor: semanticColors.surface,
    }}>
      <button type="button" data-testid="my-next-actions-toggle" aria-expanded={open} onClick={toggle} style={{
        width: "100%", minHeight: heights.control, display: "flex", alignItems: "center", gap: spacing[6],
        padding: `0 ${spacing[12]}px`, background: "none", border: "none", cursor: "pointer", textAlign: "left",
        fontSize: typography.fontSize.base, fontWeight: typography.fontWeight.semibold, color: semanticColors.textPrimary,
      }}>
        {open ? <ChevronDown size={iconSize.lg} strokeWidth={iconStroke.regular} aria-hidden />
          : <ChevronRight size={iconSize.lg} strokeWidth={iconStroke.regular} aria-hidden />}
        My next actions
        <span style={{ fontWeight: typography.fontWeight.regular, fontSize: typography.fontSize.sm, color: semanticColors.textMuted }}>
          OEM prices to confirm, customers to hear from, Won quotes to order, OEM orders that need you
        </span>
      </button>
      {open && (
        <div style={{ padding: `0 ${spacing[12]}px ${spacing[12]}px`, display: "grid", gap: spacing[12],
          gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))" }}>
          {group("My drafts with OEM prices to confirm", drafts, "None: every priced line on your drafts is confirmed.",
            draftsMore, "next-actions-drafts")}
          {group("My quotes waiting for the customer", waiting, "None: no quote of yours is waiting for a decision.",
            false, "next-actions-waiting")}
          {group("My Won quotes not yet ordered", unordered, "None: every Won quote of yours is fully ordered.",
            false, "next-actions-unordered")}
          {group("OEM orders: late, at risk or not yet acknowledged", orders, "None: no open OEM order needs you.",
            false, "next-actions-orders")}
          {error && <div role="alert" style={{ fontSize: typography.fontSize.sm, color: colors.red600 }}>
            {error} <button type="button" onClick={() => { void load(); }} style={{
              height: heights.input, padding: `0 ${spacing[10]}px`, border: `1px solid ${semanticColors.border}`,
              borderRadius: radius.base, backgroundColor: semanticColors.surface, cursor: "pointer",
            }}>Retry</button></div>}
        </div>
      )}
    </section>
  );
}
